// TelcoTrack – helpers for persisting list filters in the URL (shareable / back-button safe).

import type { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { joinMultiFilterCsv } from './filter-multi.util';
import { DEFAULT_SHOW_ACTIVE_POLYGONS_ONLY } from './active-polygon-filter.util';

/** CSV multi-value query param → unique trimmed strings. */
export function parseFilterCsvFromQuery(raw: string | null | undefined): string[] {
  const text = (raw ?? '').trim();
  if (!text) return [];
  return [...new Set(text.split(',').map((s) => s.trim()).filter(Boolean))];
}

export function setQueryCsv(p: Params, key: string, values: string[] | null | undefined): void {
  const csv = joinMultiFilterCsv(values);
  if (csv) p[key] = csv;
}

export function setQueryString(
  p: Params,
  key: string,
  value: string | null | undefined,
  omitIf?: string,
): void {
  const v = (value ?? '').trim();
  if (!v) return;
  if (omitIf !== undefined && v === omitIf) return;
  p[key] = v;
}

export function setQueryPage(p: Params, page: number): void {
  if (page > 1) p['page'] = String(page);
}

/**
 * Merge-mode setters: always write the key. Empty / default → `null` so Angular
 * removes the param (omitting the key would leave a stale value with `merge`).
 */
export function setMergeQueryString(
  p: Params,
  key: string,
  value: string | null | undefined,
  omitIf?: string,
): void {
  const v = (value ?? '').trim();
  if (!v || (omitIf !== undefined && v === omitIf)) {
    p[key] = null;
    return;
  }
  p[key] = v;
}

export function setMergeQueryCsv(
  p: Params,
  key: string,
  values: string[] | null | undefined,
): void {
  p[key] = joinMultiFilterCsv(values) ?? null;
}

export function setMergeQueryPage(p: Params, key: string, page: number): void {
  p[key] = page > 1 ? String(page) : null;
}

/** Persist `active=0` only when checkbox is off (default is on). */
export function setQueryActiveOnly(
  p: Params,
  showActiveOnly: boolean,
  defaultValue = DEFAULT_SHOW_ACTIVE_POLYGONS_ONLY,
): void {
  if (showActiveOnly !== defaultValue) {
    p['active'] = showActiveOnly ? '1' : '0';
  }
}

/**
 * Merge-mode active flag: default → `null` (remove key), otherwise `1`/`0`.
 */
export function setMergeQueryActiveOnly(
  p: Params,
  showActiveOnly: boolean,
  defaultValue = DEFAULT_SHOW_ACTIVE_POLYGONS_ONLY,
): void {
  if (showActiveOnly === defaultValue) {
    p['active'] = null;
    return;
  }
  p['active'] = showActiveOnly ? '1' : '0';
}

export function parseQueryActiveOnly(
  pm: ParamMap,
  defaultValue = DEFAULT_SHOW_ACTIVE_POLYGONS_ONLY,
): boolean {
  const raw = (pm.get('active') ?? '').trim().toLowerCase();
  if (raw === '0' || raw === 'false') return false;
  if (raw === '1' || raw === 'true') return true;
  return defaultValue;
}

/** Persist `inactive=1` only when including inactive items (default off). */
export function setQueryIncludeInactive(p: Params, includeInactive: boolean): void {
  if (includeInactive) p['inactive'] = '1';
}

export function parseQueryIncludeInactive(pm: ParamMap, defaultValue = false): boolean {
  const raw = (pm.get('inactive') ?? '').trim().toLowerCase();
  if (raw === '1' || raw === 'true') return true;
  if (raw === '0' || raw === 'false') return false;
  return defaultValue;
}

export function parseQueryPage(pm: ParamMap, fallback = 1): number {
  const raw = pm.get('page')?.trim();
  if (!raw) return fallback;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : fallback;
}

export function parseQueryString(pm: ParamMap, key: string, fallback = ''): string {
  return pm.get(key)?.trim() ?? fallback;
}

/** Detailní / Souhrn list toggle — values match existing `mode` / `wr_mode` query params. */
export type ListDisplayMode = 'detail' | 'aggregated';

/** Standalone lists (výkaz, výdejky, příjemky). */
export const LIST_DISPLAY_MODE_QUERY_KEY = 'mode';
/** Materials panel on hub child routes (polygon / user) — must not collide with `mode` / `wr_mode`. */
export const MATERIALS_DISPLAY_MODE_QUERY_KEY = 'mat_mode';

export function parseListDisplayMode(
  pm: ParamMap,
  key: string,
  defaultValue: ListDisplayMode,
): ListDisplayMode {
  const raw = (pm.get(key) ?? '').trim();
  if (raw === 'detail' || raw === 'aggregated') return raw;
  return defaultValue;
}

/** Persist display mode; omit the key when it equals the page default. */
export function setQueryDisplayMode(
  p: Params,
  key: string,
  mode: ListDisplayMode,
  defaultValue: ListDisplayMode,
): void {
  if (mode !== defaultValue) p[key] = mode;
}

/** Merge-mode display mode: default → `null` (remove key). */
export function setMergeQueryDisplayMode(
  p: Params,
  key: string,
  mode: ListDisplayMode,
  defaultValue: ListDisplayMode,
): void {
  p[key] = mode !== defaultValue ? mode : null;
}

/**
 * Replace current route query params (no history spam).
 * Pass a full params object (omitting defaults) — does not merge with previous params,
 * so cleared filters disappear from the URL.
 */
export function replaceListQueryParams(
  router: Router,
  route: ActivatedRoute,
  queryParams: Params,
): void {
  void router.navigate([], {
    relativeTo: route,
    queryParams,
    replaceUrl: true,
  });
}

/**
 * Merge a partial params patch into the current URL (for nested tab filters that
 * must keep `tab=` and sibling keys). Use `null` values to remove a key.
 */
export function mergeListQueryParams(
  router: Router,
  route: ActivatedRoute,
  queryParams: Params,
): void {
  void router.navigate([], {
    relativeTo: route,
    queryParams,
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });
}
